import { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  User,
  List,
  Moon,
  Sun,
  Home,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";
import { useMutationData } from "../../hooks/useMutationData";
import { AuthContext } from "../../context/AuthContext";
import { ThemeContext } from "../../components/ThemeContext/ThemeContext";
import { useBloquesContext } from "context/BloquesContext";

import AdminImage from "../../img/Admin.png";
import UserImage from "../../img/User.png";

export const NavbarGeneral = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEvaluadosOpen, setIsEvaluadosOpen] = useState(false);

  const { isDarkMode, toggleTheme } = useContext(ThemeContext);

  const authContext = useContext(AuthContext);
  const mutateLogOut = useMutationData({ url: "/logout" });

  const isAdmin = authContext.rol === "Administrador";
  const imagenPerfil = isAdmin ? AdminImage : UserImage;
  const navigate = useNavigate();

  const handleNavigateEvaluacionesInternos = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();

    navigate("/rol");

    setTimeout(() => {
      navigate("/evaluadoslist?tipo=internos");
    }, 50);
  };

  const handleNavigateEvaluacionesExternos = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();

    navigate("/rol");

    setTimeout(() => {
      navigate("/evaluadoslist?tipo=externos");
    }, 50);
  };

  const handleNavigateUsuarios = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    navigate("/rol");

    setTimeout(() => {
      navigate("/usuario");
    }, 50);
  };

  const playLogoutSound = () => {
    const audio = new Audio(require("../../sound/logout.wav"));
    audio.play();
  };

  const toggleAdminDropdown = () => {
    setIsAdminOpen((prev) => !prev);
    if (isProfileOpen) setIsProfileOpen(false);
    if (isEvaluadosOpen) setIsEvaluadosOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileOpen((prev) => !prev);
    if (isAdminOpen) setIsAdminOpen(false);
    if (isEvaluadosOpen) setIsEvaluadosOpen(false);
  };

  const toggleEvaluadosDropdown = () => {
    setIsEvaluadosOpen((prev) => !prev);
    if (isAdminOpen) setIsAdminOpen(false);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  const navbarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
        setIsAdminOpen(false);
        setIsProfileOpen(false);
        setIsEvaluadosOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`text-white ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
      ref={navbarRef}
    >
      <nav
        className={`flex items-center justify-between p-4 shadow-lg ${
          isDarkMode
            ? "bg-gradient-to-r from-customBlue to-blue-900"
            : "bg-gradient-to-r from-customCyan to-customBlue"
        }`}
      >
        {/* Botón de Inicio */}
        <Link to="/" className="no-underline ml-2">
          <motion.button
            className="flex items-center justify-center px-2 py-2 text-white 
                      rounded-full bg-gradient-to-r from-blue-500 to-blue-400 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <motion.div>
              <Home className="w-5 h-5" />
            </motion.div>
          </motion.button>
        </Link>

        {/* Dropdown Ver evaluados */}
        <div className="flex items-center ml-auto space-x-4">
          <div className="relative">
            <button
              onClick={toggleEvaluadosDropdown}
              className="flex items-center space-x-1 p-2 rounded-lg hover:bg-[#4a4fb8] transition-colors"
            >
              <Users className="w-5 h-5 mr-1" />
              <span>Ver evaluados</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isEvaluadosOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isEvaluadosOpen && (
              <div
                className={`absolute mt-2 w-48 rounded-md shadow-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-[#252a6d]"
                } ring-1 ring-black ring-opacity-5 z-50`}
              >
                <div
                  className="py-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <Link
                    to="/evaluadoslist?tipo=internos"
                    onClick={handleNavigateEvaluacionesInternos}
                    className="block px-4 py-2 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <User className="inline-block mr-2 w-4 h-4" /> Internos
                  </Link>
                  <Link
                    to="/evaluadoslist?tipo=externos"
                    onClick={handleNavigateEvaluacionesExternos}
                    className="block px-4 py-2 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <User className="inline-block mr-2 w-4 h-4" /> Externos
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Drop list de Administración */}
          <div className="relative">
            <button
              onClick={toggleAdminDropdown}
              className="flex items-center space-x-1 p-2 rounded-lg hover:bg-[#4a4fb8] transition-colors"
            >
              <List className="w-5 h-5 mr-1" />
              <span>Administración</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isAdminOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isAdminOpen && (
              <div
                className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-[#252a6d]"
                } ring-1 ring-black ring-opacity-5 z-50`}
              >
                <div
                  className="py-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <Link
                    to="/usuario"
                    onClick={handleNavigateUsuarios}
                    className="block px-4 py-2 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <User className="inline-block mr-2 w-4 h-4" /> Opción de
                    Usuario
                  </Link>
                  <Link
                    to="/rol"
                    className="block px-4 py-2 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <User className="inline-block mr-2 w-4 h-4" /> Opción de Rol
                  </Link>
                  <Link
                    to="/empresas"
                    className="block px-4 py-2 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <User className="inline-block mr-2 w-4 h-4" /> Opción de
                    Empresas
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Drop list de la persona logueada */}
          <div className="relative">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center space-x-2 p-2 rounded-full hover:bg-[#4a4fb8] transition-colors"
            >
              <img
                src={imagenPerfil}
                alt="Imagen de perfil"
                className="w-10 h-10 rounded-full"
              />
              <span>
                {authContext.isLoggedIn && authContext.usuario
                  ? authContext.usuario.nombre
                  : "Guest"}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isProfileOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isProfileOpen && (
              <div
                className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-[#252a6d]"
                } ring-1 ring-black ring-opacity-5 z-50`}
              >
                <div
                  className="py-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <a
                    href="#"
                    onClick={async () => {
                      await mutateLogOut.mutateAsync({ tipo: "post" });
                      playLogoutSound();
                      authContext.logout();
                    }}
                    className="block px-8 py-3 text-sm hover:bg-[#3a3f9e] transition-colors"
                    role="menuitem"
                  >
                    <LogOut className="inline-block mr-2 w-4 h-4" /> Cerrar
                    Sesión
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Interruptor del modo oscuro */}
          <button
            className={`w-12 h-6 rounded-full p-1 ${
              isDarkMode ? "bg-gray-700" : "bg-yellow-300"
            } transition-colors duration-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50`}
            onClick={toggleTheme}
            aria-label={
              isDarkMode ? "Switch to light mode" : "Switch to dark mode"
            }
          >
            <motion.div
              className="w-4 h-4 rounded-full bg-white shadow-md flex items-center justify-center"
              animate={{
                x: isDarkMode ? 24 : 0,
                rotate: isDarkMode ? 360 : 0,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {isDarkMode ? (
                <Moon className="w-3 h-3 text-gray-800" />
              ) : (
                <Sun className="w-3 h-3 text-yellow-500" />
              )}
            </motion.div>
          </button>
        </div>
      </nav>
    </div>
  );
};
